import React, { useEffect, useMemo, useState } from "react";

import i18n from "i18next";
import * as yup from "yup";
import clsx from "clsx";
import map from "lodash/map";
import find from "lodash/find";
import get from "lodash/get";
import Select from "react-select";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import Input from "../../../../components/Input";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

import { useClientId } from "../hooks/useClient";

import {
  QUERY_CLIENT_PREFERENCES,
  QUERY_CLIENT_VISITOR_FIELD,
  QUERY_CLIENT_VISITOR_FIELDS,
} from "../../../../config/graphql/query";
import {
  MUTATION_CREATE_CLIENT_VISITOR_FIELD,
  MUTATION_UPDATE_CLIENT_VISITOR_FIELD,
} from "../../../../config/graphql/mutation";

const FIELD_TYPES: ClientVisitorFieldType[] = [
  "Text",
  "Email",
  "Phone",
  "Number",
  "Dropdown",
  "Checkbox",
  "RadioButtons",
];

interface FieldValues {
  type: ClientVisitorFieldType;
  label: string;
  description?: string;
  required: boolean;
  dropdownOptions?: string[];
  checkbox?: { description?: { title?: string; text?: string }; label: string };
  radioButtons?: string[];
  hasOtherRadioButton?: boolean;
}

const removeTypename = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(removeTypename);
  if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => key !== "__typename")
        .map(([key, value]) => [key, removeTypename(value)]),
    );
  }
  return obj;
};

const TextController: React.FC<{
  name: string;
  language: string;
  isTextarea?: boolean;
}> = ({ name, language, isTextarea }) => {
  const { t } = useTranslation(["visitorPreregistration"]);
  return (
    <Controller
      name={name}
      render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
        <div className="form-group">
          <label htmlFor={name}>
            {t(`visitorPreregistration:form.${name}`)}
          </label>
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text">{language.toUpperCase()}</span>
            </div>
            {isTextarea ? (
              <textarea
                onChange={onChange}
                {...rest}
                rows={4}
                className={clsx("form-control", { "is-invalid": !!error })}
              />
            ) : (
              <input
                onChange={onChange}
                {...rest}
                className={clsx("form-control", { "is-invalid": !!error })}
              />
            )}
            {!!error && <div className="invalid-feedback">{error.message}</div>}
          </div>
        </div>
      )}
    />
  );
};

const VisitorFieldForm: React.FC = () => {
  const { t } = useTranslation([
    "visitorPreregistration",
    "common",
    "languages",
  ]);
  const history = useHistory();
  const match = useRouteMatch();
  const clientId = useClientId();
  const { fieldId } = useParams<{ fieldId?: string }>();

  const isEdit = !!fieldId && fieldId !== "new";
  const listUrl = match.url.replace(/\/(new|[^/]+)$/, "");

  const { data: clientData } = useQuery<{
    client: { preferences?: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "cache-and-network",
  });

  const enabledLanguages = useMemo<string[]>(
    () => clientData?.client?.preferences?.preRegistration?.languages ?? [],
    [clientData],
  );

  const [language, setLanguage] = useState<string>(
    enabledLanguages.includes("en") ? "en" : enabledLanguages[0] || "en",
  );

  useEffect(() => {
    if (!enabledLanguages.includes(language)) {
      setLanguage(enabledLanguages[0] || "en");
    }
  }, [enabledLanguages, language]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        type: yup
          .string()
          .oneOf(FIELD_TYPES)
          .required(t("visitorPreregistration:yup.type")),
        label: yup.string().required(t("visitorPreregistration:yup.label")),
        dropdownOptions: yup
          .array()
          .of(
            yup
              .string()
              .trim()
              .min(1, t("visitorPreregistration:yup.dropdownOptions.empty")),
          )
          .when("type", {
            is: "Dropdown",
            then: (s) =>
              s.min(1, t("visitorPreregistration:yup.dropdownOptions.min")),
            otherwise: (s) => s.notRequired().nullable().default([]),
          }),
        checkbox: yup.mixed().when("type", {
          is: "Checkbox",
          then: () =>
            yup.object().shape({
              label: yup
                .string()
                .nullable()
                .required(t("visitorPreregistration:yup.checkbox.label")),
            }),
          otherwise: () => yup.mixed().notRequired(),
        }),
        radioButtons: yup
          .array()
          .of(
            yup
              .string()
              .trim()
              .min(1, t("visitorPreregistration:yup.radioButtons.empty")),
          )
          .when("type", {
            is: "RadioButtons",
            then: (s) =>
              s.min(1, t("visitorPreregistration:yup.radioButtons.min")),
            otherwise: (s) => s.notRequired().nullable().default([]),
          }),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema) as any,
    shouldFocusError: false,
    defaultValues: {
      type: "Text",
      label: "",
      description: undefined,
      required: true,
      dropdownOptions: undefined,
      checkbox: { label: undefined as any },
      radioButtons: undefined,
      hasOtherRadioButton: undefined,
    },
  });

  const { data, loading, refetch } = useQuery<{
    clientVisitorField: IClientVisitorField;
  }>(QUERY_CLIENT_VISITOR_FIELD, {
    variables: { id: fieldId, language },
    skip: !isEdit,
    context: { headers: { ClientId: clientId } },
    onError: () => history.replace(listUrl),
  });

  useEffect(() => {
    if (isEdit && language) refetch({ id: fieldId, language });
  }, [language, fieldId, isEdit, refetch]);

  useEffect(() => {
    const f = data?.clientVisitorField;
    if (f) {
      methods.reset({
        type: f.type,
        label: f.label || "",
        description: f.description,
        required: f.required,
        dropdownOptions: f.dropdownOptions?.map((o) => o.label) ?? [],
        checkbox: f.checkbox
          ? {
              label: f.checkbox.label,
              description: f.checkbox.description,
            }
          : { label: "" },
        radioButtons: f.radioButtons?.buttons?.map((b) => b.label) ?? [],
        hasOtherRadioButton: f.radioButtons?.hasOther,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const type = methods.watch("type");
  const isDropdown = type === "Dropdown";
  const isCheckbox = type === "Checkbox";
  const isRadioButtons = type === "RadioButtons";

  useEffect(() => {
    if (!isDropdown) methods.setValue("dropdownOptions", []);
    if (!isRadioButtons) methods.setValue("radioButtons", []);
  }, [isDropdown, isRadioButtons, methods]);

  const {
    append: appendDropdown,
    remove: removeDropdown,
    fields: dropdownFields,
    // @ts-ignore
  } = useFieldArray<FieldValues, "dropdownOptions">({
    control: methods.control,
    name: "dropdownOptions",
  });

  const {
    append: appendRadio,
    remove: removeRadio,
    fields: radioFields,
    // @ts-ignore
  } = useFieldArray<FieldValues, "radioButtons">({
    control: methods.control,
    name: "radioButtons",
  });

  const listLanguage = i18n.language || "en";
  const [onCreate] = useMutation(MUTATION_CREATE_CLIENT_VISITOR_FIELD, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_VISITOR_FIELDS,
        variables: { language: listLanguage },
      },
    ],
    context: { headers: { ClientId: clientId } },
  });
  const [onUpdate] = useMutation(MUTATION_UPDATE_CLIENT_VISITOR_FIELD, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_VISITOR_FIELDS,
        variables: { language: listLanguage },
      },
      ...(isEdit
        ? [
            {
              query: QUERY_CLIENT_VISITOR_FIELD,
              variables: { id: fieldId, language },
            },
          ]
        : []),
    ],
    context: { headers: { ClientId: clientId } },
  });

  const onSubmit = async (values: FieldValues) => {
    const input = {
      language,
      type: values.type,
      label: values.label,
      ...(typeof values.description === "string" && {
        description: values.description,
      }),
      required: values.required,
      ...(isDropdown && { dropdownOptions: values.dropdownOptions }),
      ...(isCheckbox && { checkbox: removeTypename(values.checkbox) }),
      ...(isRadioButtons && {
        radioButtons: values.radioButtons,
        hasOtherRadioButton: values.hasOtherRadioButton,
      }),
    };

    try {
      if (isEdit) {
        await onUpdate({ variables: { input: { id: fieldId, ...input } } });
        toast.success<string>(t("visitorPreregistration:toast.updated"));
      } else {
        await onCreate({ variables: { input } });
        toast.success<string>(t("visitorPreregistration:toast.created"));
        history.replace(listUrl);
      }
    } catch (e: any) {
      toast.error<string>(
        e?.networkError?.result?.errors?.[0]?.message || e?.message,
      );
    }
  };

  const typeOptions = useMemo(
    () => map(FIELD_TYPES, (v) => ({ value: v, label: v })),
    [],
  );
  const langOptions = useMemo(
    () =>
      map(enabledLanguages, (v) => ({ value: v, label: t(`languages:${v}`) })),
    [t, enabledLanguages],
  );

  const renderOption = (
    kind: "dropdown" | "radio",
    field: any,
    index: number,
  ) => (
    <div key={field.id} className="d-flex mb-3">
      <div className="flex-grow-1">
        <div className="input-group">
          <div className="input-group-prepend">
            <span className="input-group-text">{language.toUpperCase()}</span>
          </div>
          <Input
            {...methods.register(
              `${
                kind === "dropdown" ? "dropdownOptions" : "radioButtons"
              }.${index}` as any,
            )}
            className="form-control"
            defaultValue={field as any}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          if (kind === "dropdown") removeDropdown(index);
          else removeRadio(index);
        }}
        className="btn btn-danger ml-3 align-self-start"
      >
        {t("common:remove")}
      </button>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="mb-3">
        <h5>
          {isEdit
            ? t("visitorPreregistration:form.editTitle")
            : t("visitorPreregistration:form.createTitle")}
        </h5>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-lg-6 col-md-12">
              <div className="form-group">
                <label htmlFor="language">
                  {t("visitorPreregistration:form.language")}
                </label>
                <Select
                  options={langOptions}
                  value={langOptions.find((o) => o.value === language)}
                  onChange={(v) => v && setLanguage(v.value)}
                  styles={reactSelectCustomStyles(false)}
                />
                <small className="text-muted font-italic d-block mt-1">
                  {t("visitorPreregistration:form.languageHelp")}
                </small>
              </div>

              <Controller
                name="type"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="form-group">
                    <label htmlFor="type">
                      {t("visitorPreregistration:form.type")}
                    </label>
                    <Select
                      options={typeOptions}
                      onChange={(v) => onChange(get(v, "value"))}
                      value={find(typeOptions, { value })}
                      className={clsx({ "is-invalid": !!error })}
                    />
                    {!!error && (
                      <div className="invalid-feedback">{error.message}</div>
                    )}
                  </div>
                )}
              />

              <TextController name="label" language={language} />
              <TextController
                name="description"
                language={language}
                isTextarea
              />

              <Controller
                name="required"
                render={({ field: { value, name, onChange, ...rest } }) => (
                  <div className="form-check my-3">
                    <input
                      id={name}
                      {...rest}
                      type="checkbox"
                      className="form-check-input"
                      checked={value === true}
                      onChange={() => onChange(!value)}
                    />
                    <label className="form-check-label" htmlFor={name}>
                      {t("visitorPreregistration:form.required")}
                    </label>
                  </div>
                )}
              />

              {isDropdown && (
                <>
                  <h6>{t("visitorPreregistration:form.dropdownOptions")}</h6>
                  {dropdownFields.map((f, i) => renderOption("dropdown", f, i))}
                  <button
                    type="button"
                    className="btn btn-secondary mb-2"
                    onClick={() => appendDropdown("")}
                  >
                    {t("visitorPreregistration:button.addDropdownOption")}
                  </button>
                </>
              )}

              {isCheckbox && (
                <>
                  <TextController name="checkbox.label" language={language} />
                  <TextController
                    name="checkbox.description.title"
                    language={language}
                  />
                  <TextController
                    name="checkbox.description.text"
                    language={language}
                    isTextarea
                  />
                </>
              )}

              {isRadioButtons && (
                <>
                  <h6>{t("visitorPreregistration:form.radioButtons")}</h6>
                  <Controller
                    name="hasOtherRadioButton"
                    render={({ field: { value, ...rest } }) => (
                      <div className="form-check mb-3">
                        <input
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="hasOtherRadioButton"
                        >
                          {t("visitorPreregistration:form.hasOtherRadioButton")}
                        </label>
                      </div>
                    )}
                  />
                  {radioFields.map((f, i) => renderOption("radio", f, i))}
                  <button
                    type="button"
                    className="btn btn-secondary mb-2"
                    onClick={() => appendRadio("")}
                  >
                    {t("visitorPreregistration:button.addRadioButton")}
                  </button>
                </>
              )}

              <div className="d-flex mt-4">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => history.push(listUrl)}
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {t("common:save")}
                </button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default VisitorFieldForm;
